/**
 * The Media Session the player drives (lock screen, notification shade,
 * headset buttons), behind one interface:
 * - in a browser and in the iOS app, the web API (`navigator.mediaSession`),
 *   which WKWebView turns into the lock-screen controls;
 * - in the Android app, @capgo/capacitor-media-session: Android's WebView
 *   keeps the audio playing screen off but shows no controls, so a native
 *   media session service (foreground, mediaPlayback) shows them instead.
 *
 * Same calls as the web API. The Android service decodes artwork from an
 * http(s) or base64 bitmap URL only (no SVG, no blob:), so there the
 * story's SVG cover gives way to the app icon.
 */
import { Capacitor } from "@capacitor/core";
import { MediaSession as NativeMediaSession } from "@capgo/capacitor-media-session";
import { publicOrigin } from "./platform";

export type Artwork = { src: string; sizes?: string; type?: string };
export type Action = "play" | "pause" | "seekbackward" | "seekforward";

const native = Capacitor.getPlatform() === "android";

function web(): MediaSession | null {
  return "mediaSession" in navigator ? navigator.mediaSession : null;
}

export function available(): boolean {
  return native || web() !== null;
}

export function setMetadata(meta: { title: string; artist: string; album?: string; artwork: Artwork[] }): void {
  if (native) {
    const bitmap = meta.artwork.find((a) => /^https?:/.test(a.src));
    const artwork = [bitmap ?? { src: `${publicOrigin()}/icon-512.png`, sizes: "512x512", type: "image/png" }];
    void NativeMediaSession.setMetadata({ ...meta, artwork }).catch(() => {});
    return;
  }
  const session = web();
  if (session) session.metadata = new MediaMetadata(meta);
}

export function setPlaybackState(playbackState: MediaSessionPlaybackState): void {
  if (native) {
    void NativeMediaSession.setPlaybackState({ playbackState }).catch(() => {});
    return;
  }
  const session = web();
  if (!session) return;
  try {
    session.playbackState = playbackState;
  } catch {
    /* not implemented everywhere Media Session itself is */
  }
}

export function setPositionState(state: { duration: number; position: number; playbackRate: number }): void {
  if (native) {
    void NativeMediaSession.setPositionState(state).catch(() => {});
    return;
  }
  const session = web();
  if (!session || typeof session.setPositionState !== "function") return;
  // iOS can throw on a torn-down element mid-reload; the caller retries on
  // the next loadedmetadata/play/pause/seeked with fresh state.
  session.setPositionState(state);
}

export function setActionHandler(action: Action, handler: (() => void) | null): void {
  if (native) {
    void NativeMediaSession.setActionHandler({ action }, handler && (() => handler())).catch(() => {});
    return;
  }
  web()?.setActionHandler(action, handler);
}
